import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { UK_CITIES } from '../../lib/ukCities';

/**
 * CityPinTicker
 *
 * Thin editorial band that sits between the hero and the Bento grid.
 * Carries the UK-wide positioning: a strap-line and a slow horizontal
 * marquee of city names separated by gold map pins, so a visitor can't
 * miss "we're everywhere now" before they reach the founder letter.
 *
 * The marquee uses pure CSS keyframes (duplicated city row + translateX
 * so the loop is seamless). We respect `prefers-reduced-motion` by
 * pausing the animation.
 */

const CITIES = UK_CITIES;

function CityRow({ ariaHidden }) {
  return (
    <ul
      aria-hidden={ariaHidden}
      className="flex items-center gap-10 pr-10 shrink-0 font-heading text-lg tracking-wide text-white/90"
    >
      {CITIES.map((city) => (
        <li key={city.slug} className="flex items-center gap-10 whitespace-nowrap">
          <Link
            to={`/discover?city=${city.slug}`}
            data-testid={`ticker-city-${city.slug}`}
            tabIndex={ariaHidden ? -1 : 0}
            aria-label={`See providers within 20 miles of ${city.label}`}
            className="flex items-center gap-2 group hover:text-[#D4AF37] transition-colors"
          >
            <MapPin className="h-4 w-4 text-[#D4AF37] group-hover:scale-110 transition-transform" aria-hidden="true" />
            <span className="group-hover:underline underline-offset-4 decoration-[#D4AF37]/60">
              {city.label}
            </span>
          </Link>
          <span aria-hidden="true" className="text-[#D4AF37]/60">
            &middot;
          </span>
        </li>
      ))}
    </ul>
  );
}

export default function CityPinTicker() {
  return (
    <section
      className="relative bg-[#1A1A1A] text-white border-y border-white/10 py-7 overflow-hidden"
      data-testid="city-pin-ticker"
      aria-label="Zuriya operates across the UK"
    >
      {/* Keyframes scoped inline so the component is self-contained
          and doesn't depend on tailwind.config edits. Pause-on-hover
          lets users actually click a specific city without chasing a
          moving target. */}
      <style>{`
        @keyframes zuriya-ticker-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        .zuriya-ticker-track {
          animation: zuriya-ticker-scroll 55s linear infinite;
        }
        .zuriya-ticker-rail:hover .zuriya-ticker-track,
        .zuriya-ticker-rail:focus-within .zuriya-ticker-track {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .zuriya-ticker-track { animation: none; }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-6 lg:px-20">
        <p
          className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-3 text-center lg:text-left"
          data-testid="ticker-strapline"
        >
          Serving stylists and clients across the UK
        </p>
      </div>

      {/* Edge-to-edge marquee rail */}
      <div className="relative zuriya-ticker-rail">
        {/* Soft fade mask on the sides so cities glide in / out,
            no abrupt cut at the viewport edge. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-24 bg-gradient-to-r from-[#1A1A1A] to-transparent z-10"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-24 bg-gradient-to-l from-[#1A1A1A] to-transparent z-10"
        />

        <div className="flex zuriya-ticker-track" style={{ width: 'max-content' }}>
          <CityRow ariaHidden={false} />
          {/* Duplicate row so the -50% translate loops seamlessly. */}
          <CityRow ariaHidden={true} />
        </div>
      </div>
    </section>
  );
}
